// A site's pages as plain HTML, or in its terminal: the slider in the
// sites' bar, web | hotty | tui (shared/sitebar's BarModes, wired by its
// modes.ts), on every page of a site in the sites' shell (its overview and
// its docs, laid out by docs/internal/site/shell.go).
//   page    plain HTML
//   hotty   xterm.js with the hotty addon: the site's program prints its
//           pages as HTML in the terminal
//   text    xterm.js alone: the program draws them in cells, as any
//           terminal shows them
// The reader's last choice is theirs across neuroplast.io's sites (the
// bar's tui.ts, a cookie); with none, the terminal is theirs, hotty. The
// terminal (site-term.js, term.ts: xterm.js, the addon, the program) loads
// only when one is chosen: the page is plain HTML for everyone else, and
// for everyone until the terminal has drawn, crawlers included. A site's
// build bundles this as /site-mode.js, in the head of every page.
//
// The page stays until the terminal has drawn: a terminal on its way
// (data-loading) is laid out where it goes, unseen, and takes the page's
// place when its bridge says it has drawn its first screen
// (site-term-drawn), open at the section the reader is in (siteSection).
// Until then the page reads and scrolls as ever, and the chosen button says
// the terminal is coming. The switch's state (data-switch) is set here, in
// the head, so that the first paint has it. Pointing at a terminal's
// button starts the program's download (siteWasm, the most of the wait),
// which the terminal takes over. One terminal for the other is a switch in
// place (siteTerm.mode): the terminal fades, the program changes viewers,
// and the other's first screen comes back in.
//
// One page of the site for another is in place too, in every mode: as
// plain HTML (pages, below), the page's parts swapped; in the terminal,
// the header's links go to the program, which shows the page.
//
// This runs in the head, before the body is parsed: nothing here may
// throw.

import * as modes from "../sitebar/modes";
import { choose, chosen as kept, type Mode } from "../sitebar/tui";
import * as toc from "./toc";

// What this script shares with the terminal's (term.ts) on window.
type Site = Window & {
  siteWasm?: Promise<Response>;
  siteTermMode?: "hotty" | "text";
  siteTerm?: {
    show(): void;
    hide(): void;
    focus(): void;
    mode(m: "hotty" | "text"): void;
  };
  siteSection?: () => string;
  // The header's link to the page at path lit (term.ts, as the program
  // shows a page).
  siteLit?: (path: string) => void;
  // The program, once it runs: go shows another page.
  site?: { go(path: string): void };
};
const w = window as Site;

const root = document.documentElement;
root.classList.add("js");
// The page's contents, told the page is shown again (spy's).
let requeue = () => {};
w.siteLit = lit;
if (root.hasAttribute("data-site")) {
  // The page's own address, where it was asked for at another: the docs'
  // root shows their first page, whose address its page names.
  document.addEventListener("DOMContentLoaded", () => {
    const at = document.body.dataset.path;
    if (at && at !== location.pathname) history.replaceState(history.state, "", at + location.hash);
  });
  site();
  pages();
}
document.addEventListener("DOMContentLoaded", () => spy());
contents();

// Where the site's pages are, as the terminal's place says: the program,
// the docs' root and the overview's path.
function where() {
  const d = document.getElementById("site-term")?.dataset ?? {};
  return {
    program: d.program ?? "",
    root: d.root || "/docs/",
    home: d.home ?? "",
  };
}

function site() {
  const chosen: Mode = kept() ?? "hotty";
  // What is shown, and what the switch says.
  root.dataset.mode = "page";
  root.dataset.switch = chosen;
  if (chosen !== "page") root.dataset.loading = "";

  // The terminal this document started, if any ("hotty" or "text"),
  // whether it has drawn, and where the page was when it gave the terminal
  // its place: its scroll, and its address's fragment.
  let started: "" | "hotty" | "text" = "";
  let drawn = false;
  let left: { y: number; hash: string } | null = null;
  // How long the terminal takes to fade, before a switch in place.
  const FADE = matchMedia("(prefers-reduced-motion: reduce)").matches ? 0 : 120;

  function warm() {
    const { program } = where();
    if (w.siteWasm || !program) return;
    w.siteWasm = fetch(program);
    w.siteWasm.catch(() => {}); // the terminal fetches it again
  }
  function startTerminal(mode: "hotty" | "text") {
    if (started) return;
    started = mode;
    root.dataset.term = "";
    w.siteTermMode = mode;
    warm();
    const css = document.createElement("link");
    css.rel = "stylesheet";
    css.href = "/site-term.css";
    document.head.append(css);
    const js = document.createElement("script");
    js.src = "/site-term.js";
    document.body.append(js);
  }

  // The terminal takes the page's place, from the window's top; or, after
  // a switch in place, shows the other mode's first screen.
  function reveal() {
    const fromPage = root.dataset.mode === "page";
    if (fromPage) left = { y: window.scrollY, hash: location.hash };
    delete root.dataset.loading;
    root.dataset.mode = started;
    modes.check(started as Mode);
    if (fromPage) window.scrollTo(0, 0);
    w.siteTerm?.show();
  }

  // The page again, where it was; or at the heading the terminal went to,
  // if it went to one. The terminal stays under it, laid out as it was
  // (data-term), so that it comes back as it went.
  function back() {
    root.dataset.mode = "page";
    w.siteTerm?.hide();
    requeue();
    if (!left) return;
    const id = location.hash !== left.hash ? decodeURIComponent(location.hash.slice(1)) : "";
    const h = id && document.getElementById(id);
    if (h) h.scrollIntoView();
    else window.scrollTo(0, left.y);
  }

  function show(mode: Mode, chosenNow: boolean) {
    if (chosenNow) choose(mode);
    // The terminal may have gone to another page (history.pushState): the
    // page there takes the shown one's place (go), and the terminal stays
    // until it has, unless the reader chose it again meanwhile.
    const moved = location.pathname !== document.body.dataset.path;
    if (mode === "page" && started && moved) {
      root.dataset.switch = mode;
      modes.check(mode, true);
      void go(location.href, "here", () => {
        if (root.dataset.switch !== "page") return false;
        delete root.dataset.loading;
        modes.check(mode);
        left = null;
        back();
        return true;
      });
      return;
    }
    root.dataset.switch = mode;
    if (mode === "page") {
      delete root.dataset.loading;
      modes.check(mode);
      if (root.dataset.mode !== "page") back();
      return;
    }
    // One terminal for the other, in place: a shown terminal fades first.
    if (started && mode !== started) {
      started = mode;
      w.siteTermMode = mode;
      drawn = false;
      root.dataset.loading = "";
      modes.check(mode, true);
      if (root.dataset.mode === "page") w.siteTerm?.mode(mode);
      else {
        root.dataset.mode = mode;
        setTimeout(() => w.siteTerm?.mode(mode), FADE);
      }
      return;
    }
    if (drawn) {
      reveal();
      return;
    }
    root.dataset.loading = "";
    modes.check(mode, true);
    startTerminal(mode);
  }

  document.addEventListener("site-term-drawn", () => {
    drawn = true;
    if (root.dataset.switch === started) reveal();
  });
  document.addEventListener("DOMContentLoaded", () => {
    modes.slider((mode) => show(mode, true));
    for (const b of document.querySelectorAll<HTMLElement>(".npio-modes [data-mode]")) {
      // The page the terminal went to, fetched ahead as the program is.
      if (b.dataset.mode === "page") {
        const ahead = () => started && location.pathname !== document.body.dataset.path && void fetched(location.pathname);
        for (const e of ["pointerenter", "focus", "touchstart"]) b.addEventListener(e, ahead, { passive: true });
        continue;
      }
      for (const e of ["pointerenter", "focus", "touchstart"]) b.addEventListener(e, warm, { once: true, passive: true });
    }
    show(chosen, false);
  });
}

// One page of the site for another, in place: a link to a page of the
// site (its overview, a page of its docs: the header's, the nav's, the
// pager's, the page's own) fetches it, and its parts take the shown one's.
// The rest of the document stays: the bar, the header, whose link to the
// page is lit, the docs' nav from one page of the docs to the next,
// scrolled where it was, the focus in it, and the terminal, if one was
// started, which goes to the page too. The address and the history are
// the browser's (pushState), and each page's entry keeps where the window
// was (its state's y), for back and forward to come back to. Pointing at a
// link fetches its page ahead. What isn't a page of the site, or is one of
// another build (its stylesheet isn't this one's), loads as ever; so does a
// click that asks for a new tab.
//
// In the terminal, the page is the program's: the header's links go to it
// (go), and back and forward too (term.ts).
type How = "push" | "pop" | "here"; // a link, back or forward, the terminal's page

function pages() {
  document.addEventListener("click", (e) => {
    const a = link(e.target);
    if (!a || e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    if ((a.target && a.target !== "_self") || a.hasAttribute("download")) return;
    const url = new URL(a.href);
    if (root.dataset.mode !== "page") {
      // The terminal shows the page: the header's links are the program's.
      if (!a.closest(".site-top") || !page(url) || !w.site) return;
      e.preventDefault();
      const to = url.pathname + url.hash;
      if (to !== location.pathname + location.hash) history.pushState({ site: to }, "", to);
      lit(url.pathname);
      try {
        w.site.go(to);
      } catch (err) {
        console.warn("site: the terminal didn't follow:", err);
      }
      w.siteTerm?.focus();
      return;
    }
    if (url.origin === location.origin && url.pathname === location.pathname && !url.search) {
      if (url.hash) return; // a heading of this page: the browser's
      // This page, from its top.
      e.preventDefault();
      if (location.hash) {
        manual();
        remember();
        history.pushState({ site: url.pathname }, "", url.pathname);
      }
      scrollTo(0, 0);
      return;
    }
    if (!page(url)) return;
    e.preventDefault();
    void go(url.href, "push");
  });
  // Ahead: a link pointed at for a moment, or pressed.
  let ahead = 0;
  const point = (e: Event) => {
    clearTimeout(ahead);
    const a = link(e.target);
    if (!a || root.dataset.mode !== "page") return;
    const url = new URL(a.href);
    if (url.pathname === location.pathname || !page(url)) return;
    if (e.type === "pointerdown") void fetched(url.pathname);
    else ahead = setTimeout(() => void fetched(url.pathname), 65);
  };
  for (const e of ["pointerover", "pointerdown", "focusin"]) document.addEventListener(e, point, { passive: true });

  addEventListener("popstate", () => {
    clearTimeout(still); // its y is the entry's left
    if (root.dataset.mode !== "page") return; // the terminal's (term.ts)
    if (location.pathname !== document.body.dataset.path) void go(location.href, "pop");
    else if (history.scrollRestoration === "manual") place("pop", true);
  });
  addEventListener(
    "scroll",
    () => {
      clearTimeout(still);
      still = setTimeout(remember, 150);
    },
    { passive: true },
  );
  // A page this document went to, loaded again: where the window was.
  document.addEventListener("DOMContentLoaded", () => {
    const y = history.state?.y;
    if (history.scrollRestoration === "manual" && typeof y === "number") scrollTo(0, y);
  });
}

// The link an event is on, if it's on one.
function link(t: EventTarget | null) {
  const a = t instanceof Element ? t.closest("a[href]") : null;
  return a instanceof HTMLAnchorElement ? a : null;
}

// A page of the site: its overview, the docs' root (/docs, the folder
// /docs/ without its slash), or a page under it. A file there (an image, a
// page's Markdown) is none.
function page(url: URL) {
  if (url.origin !== location.origin || url.search) return false;
  const { root: docs, home } = where();
  const p = url.pathname;
  if (home && p === home) return true;
  return (p === docs.replace(/\/$/, "") || p.startsWith(docs)) && !/\.[a-z0-9]+$/i.test(p);
}

// The header's link to the page at path lit, and no other: the
// overview's ("/") for it alone, else the one whose path is the longest
// that path starts with (the docs', for any page of theirs).
function lit(path: string) {
  const links = [...document.querySelectorAll<HTMLAnchorElement>(".site-top .site-nav a[href]")];
  let on: HTMLAnchorElement | null = null;
  for (const a of links) {
    if (a.origin !== location.origin) continue;
    const p = a.pathname;
    const match = p === "/" || p === where().home ? path === p : path.startsWith(p);
    if (match && (!on || p.length > on.pathname.length)) on = a;
  }
  for (const a of links) a.classList.toggle("on", a === on);
}

// Back and forward are the pages' (place) once one has gone in place: the
// browser would put the window where it was before the page came.
function manual() {
  history.scrollRestoration = "manual";
}

// Where the window is on the shown page, in its entry: kept as the reader
// stops scrolling, and as they leave it.
let still = 0;
function remember() {
  clearTimeout(still);
  if (root.dataset.mode !== "page") return;
  history.replaceState({ ...history.state, y: Math.round(scrollY) }, "");
}

// Pages fetched, for a minute: one pointed at, then clicked, is fetched once.
const pagesFetched = new Map<string, { at: number; got: Promise<{ url: string; html: string }> }>();
function fetched(path: string) {
  const had = pagesFetched.get(path);
  if (had && performance.now() - had.at < 60_000) return had.got;
  const got = fetch(path).then(async (r) => {
    if (!r.ok) throw new Error(`${r.status} ${path}`);
    return { url: r.url, html: await r.text() };
  });
  pagesFetched.delete(path);
  pagesFetched.set(path, { at: performance.now(), got });
  got.catch(() => pagesFetched.get(path)?.got === got && pagesFetched.delete(path));
  if (pagesFetched.size > 16) pagesFetched.delete(pagesFetched.keys().next().value!);
  return got;
}

// go shows the page at href in the shown one's place, and says whether it
// did: a later go, or a page that loads as ever, is no. shown, for the
// terminal's page (here), shows the page, or says the reader is in the
// terminal again. A page that names another address as its own (the docs'
// root, which shows their first page) is at that address.
let going = 0;
async function go(href: string, how: How, shown?: () => boolean) {
  const n = ++going;
  const url = new URL(href);
  const slow = setTimeout(() => (root.dataset.going = ""), 120);
  let doc: Document | null = null;
  let to = url;
  try {
    const got = await fetched(url.pathname);
    doc = new DOMParser().parseFromString(got.html, "text/html");
    const at = new URL(got.url || url.href); // where a redirect went
    if (at.pathname !== url.pathname) to = new URL(at.pathname + url.hash, url);
    const named = doc.body.dataset.path;
    if (named && named !== to.pathname) to = new URL(named + url.hash, url);
  } catch (e) {
    console.warn("site: the page didn't come:", e);
  }
  if (n !== going) return false;
  clearTimeout(slow);
  delete root.dataset.going;
  const load = () => (how === "push" ? location.assign(to.href) : location.replace(to.href));
  if (!doc || !ours(doc)) {
    load();
    return false;
  }
  manual();
  if (how === "push") {
    remember();
    history.pushState({ site: to.pathname + to.hash }, "", to.href);
  } else if (to.href !== location.href) history.replaceState({ ...history.state, site: to.pathname + to.hash }, "", to.href);
  const cast = doc.querySelector('script[src="/docs-cast.js"]') !== null;
  try {
    swap(doc);
  } catch (e) {
    console.warn("site: the page didn't take the shown one's place:", e);
    location.reload();
    return false;
  }
  if (!shown || shown()) place(how, false);
  spy();
  const h1 = document.querySelector<HTMLElement>("#site-page h1");
  said(h1?.textContent ?? document.title);
  // The focus in a part gone (the pager), or the terminal's: the page's
  // heading has it.
  const at = document.activeElement;
  if (h1 && root.dataset.mode === "page" && (!at || at === document.body || !at.isConnected)) {
    h1.tabIndex = -1;
    h1.focus({ preventScroll: true });
  }
  document.dispatchEvent(new Event("site-page"));
  if (cast && !document.querySelector('script[src="/docs-cast.js"]')) {
    const js = document.createElement("script");
    js.src = "/docs-cast.js";
    document.body.append(js);
  }
  // A terminal under the page goes there too, as it does back and forward
  // (term.ts).
  if (how === "push" && root.hasAttribute("data-term")) {
    try {
      w.site?.go(location.pathname + location.hash);
    } catch (e) {
      console.warn("site: the terminal didn't follow:", e);
    }
  }
  return true;
}

// A page of the site, from this build: its stylesheet is this one's.
function ours(doc: Document) {
  const css = (d: Document) => d.querySelector("head style")?.textContent;
  return doc.documentElement.hasAttribute("data-site") && doc.getElementById("site-page") !== null && css(doc) === css(document);
}

// What the head says of the page, a page's own.
const HEAD = [
  'meta[name="description"]',
  'meta[name="robots"]',
  'link[rel="canonical"]',
  'meta[property="og:title"]',
  'meta[property="og:description"]',
  'meta[property="og:url"]',
  'link[rel="alternate"][type="text/markdown"]',
];

// swap puts doc's page in the shown one's place: its head's lines, and
// the page's parts. From one page to another, the overview among them,
// the columns but the nav, which says which page is shown; from or to a
// page without the nav, all of them. The header's link to the page is
// lit.
function swap(doc: Document) {
  document.title = doc.title;
  for (const sel of HEAD) {
    const was = document.head.querySelector(sel);
    const is = doc.head.querySelector(sel);
    if (was && is) was.replaceWith(is);
    else if (is) document.head.append(is);
    else was?.remove();
  }
  document.body.dataset.path = doc.body.dataset.path ?? "";
  root.removeAttribute("data-contents");
  const shown = document.getElementById("site-page")!;
  const next = doc.getElementById("site-page")!;
  // What's for readers without JavaScript is text, as the page's parser
  // makes it: a parsed document has no scripts, and its noscript holds
  // elements (the screencast's GIF), which would load.
  for (const ns of next.querySelectorAll("noscript")) {
    const t = document.createElement("noscript");
    for (const at of ns.attributes) t.setAttribute(at.name, at.value);
    t.textContent = ns.innerHTML;
    ns.replaceWith(t);
  }
  lit(location.pathname);
  const cols = shown.querySelector("#docs-columns");
  const colsNext = next.querySelector("#docs-columns");
  const nav = cols?.querySelector(".docs-nav");
  const navNext = colsNext?.querySelector(".docs-nav");
  if (!cols || !colsNext || !nav || !navNext) {
    shown.replaceChildren(...next.childNodes);
    return;
  }
  renav(nav as HTMLElement, navNext);
  for (const el of [...cols.children]) if (el !== nav) el.remove();
  let before = true;
  for (const el of [...colsNext.children]) {
    if (el === navNext) before = false;
    else if (before) nav.before(el);
    else cols.append(el);
  }
}

// The nav says which page is shown, and shows it, scrolled no more than
// it takes; another nav (other docs) takes its place, at the scroll it had.
function renav(nav: HTMLElement, next: Element) {
  const links = [...nav.querySelectorAll("a")];
  const to = [...next.querySelectorAll("a")];
  const same =
    links.length === to.length && links.every((a, i) => a.getAttribute("href") === to[i].getAttribute("href") && a.textContent === to[i].textContent);
  if (!same) {
    const y = nav.scrollTop;
    nav.replaceChildren(...next.childNodes);
    nav.scrollTop = y;
    return;
  }
  links.forEach((a, i) => {
    const c = to[i].getAttribute("aria-current");
    if (c) a.setAttribute("aria-current", c);
    else a.removeAttribute("aria-current");
  });
  const on = nav.querySelector('a[aria-current="page"]');
  if (!on) return;
  const box = nav.getBoundingClientRect();
  const r = on.getBoundingClientRect();
  if (r.top < box.top || r.bottom > box.bottom) nav.scrollTop += r.top - box.top - box.height / 3;
}

// The window on the page gone to: where it was, back or forward; else at
// the heading the address names, or the page's top. The same page, back
// or forward to a heading that isn't there, stays.
function place(how: How, same: boolean) {
  const y = history.state?.y;
  if (how === "pop" && typeof y === "number") return scrollTo(0, y);
  const id = decodeURIComponent(location.hash.slice(1));
  const h = id ? document.getElementById(id) : null;
  if (h) h.scrollIntoView();
  else if (!id || !same) scrollTo(0, 0);
}

// A screen reader is told the page gone to, as a page loaded is.
function said(what: string) {
  let el = document.getElementById("site-said");
  if (!el) {
    el = document.createElement("p");
    el.id = "site-said";
    el.setAttribute("role", "status");
    document.body.append(el);
  }
  el.textContent = what;
}

// The nav on a phone: the page alone under its line (☰ Contents · Start ›
// Overview), as the terminal's viewers have it, and the nav under the line
// on asking (data-contents, docs.css), and away again; Escape puts it away
// too. Without this the link's fragment shows it (:target).
function contents() {
  document.addEventListener("click", (e) => {
    const a = e.target instanceof Element ? e.target.closest<HTMLAnchorElement>('.docs-crumbs a[href="#docs-nav"]') : null;
    if (!a || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    e.preventDefault();
    a.setAttribute("aria-expanded", String(root.toggleAttribute("data-contents")));
  });
  document.addEventListener("keydown", (e) => {
    if (e.key !== "Escape" || !root.hasAttribute("data-contents")) return;
    root.removeAttribute("data-contents");
    const a = document.querySelector<HTMLAnchorElement>('.docs-crumbs a[href="#docs-nav"]');
    a?.setAttribute("aria-expanded", "false");
    a?.focus();
  });
}

// On this page follows the reader (toc.ts), for each page shown (go), the
// last page's listeners put away; the overview has no contents.
let spied: AbortController | null = null;
function spy() {
  spied?.abort();
  spied = new AbortController();
  const c = toc.follow(() => root.dataset.mode === "page", spied.signal);
  w.siteSection = c?.section;
  requeue = c?.queue ?? (() => {});
}
